import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabase";

const C = {
  bg: '#020617', surface: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.10)',
  accent: '#60A5FA', accentGlow: 'rgba(96,165,250,0.15)',
  gold: '#F0A500', green: '#34D399', red: '#F87171',
  text: '#FFFFFF', textMuted: '#94A3B8',
  textFaint: '#475569',
};
const F = { display: "'Playfair Display', Georgia, serif", body: "'DM Sans', sans-serif" };

const ADMIN_EMAILS = ["admin@nilamlabs.ai", "jesuworld@gmail.com"];

export function AdminPage({ user }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [actionNote, setActionNote] = useState("");
  const [processing, setProcessing] = useState(null);

  const isAdmin = ADMIN_EMAILS.includes(user?.primaryEmailAddress?.emailAddress);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("courses")
      .select("id, title, category, level, price, duration, description, thumbnail, mux_playback_id, status, created_at, creator_id")
      .eq("status", filter)
      .order("created_at", { ascending: false });
    setCourses(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleAction = async (course, action) => {
    setProcessing(course.id);
    const newStatus = action === "approve" ? "published" : "rejected";

    await supabase.from("courses").update({
      status: newStatus,
      published_at: action === "approve" ? new Date().toISOString() : null,
    }).eq("id", course.id);

    await supabase.from("course_reviews").insert({
      course_id: course.id,
      reviewer_id: user?.id,
      action,
      note: actionNote,
    });

    setProcessing(null);
    setActionNote("");
    loadCourses();
  };

  if (!isAdmin) {
    return (
      <div style={{ textAlign: "center", padding: "80px 24px", fontFamily: F.body }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontFamily: F.display, fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Access Denied</h2>
        <p style={{ color: C.textMuted, fontSize: 14 }}>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ maxWidth: 1000, margin: "0 auto", padding: "32px 24px 80px", fontFamily: F.body }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8 }}>Admin</div>
        <h1 style={{ fontFamily: F.display, fontSize: 32, fontWeight: 900, marginBottom: 6 }}>Course Approvals</h1>
        <p style={{ color: C.textMuted, fontSize: 13 }}>Review and approve creator course submissions</p>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {["pending", "published", "rejected"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${filter === f ? C.accent : C.border}`, background: filter === f ? C.accentGlow : "transparent", color: filter === f ? C.accent : C.textMuted, fontSize: 13, fontWeight: filter === f ? 700 : 500, cursor: "pointer", fontFamily: F.body, textTransform: "capitalize" }}>
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: C.textMuted, fontSize: 14, padding: "40px 0" }}>Loading...</div>
      ) : courses.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: C.textMuted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 14 }}>No {filter} courses</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {courses.map(course => (
            <div key={course.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden" }}>
              {/* Top color bar */}
              <div style={{ height: 3, background: filter === "pending" ? C.gold : filter === "published" ? C.green : C.red }} />
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  {/* Thumbnail */}
                  {course.thumbnail ? (
                    <img src={course.thumbnail} alt={course.title} style={{ width: 80, height: 48, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 80, height: 48, borderRadius: 8, background: C.accentGlow, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>📹</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{course.title}</h3>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: C.textMuted }}>📁 {course.category}</span>
                      <span style={{ fontSize: 12, color: C.textMuted }}>📊 {course.level}</span>
                      <span style={{ fontSize: 12, color: course.price > 0 ? C.gold : C.green }}>
                        {course.price > 0 ? `$${course.price}` : "Free"}
                      </span>
                      {course.duration && <span style={{ fontSize: 12, color: C.textMuted }}>⏱ {course.duration}</span>}
                      <span style={{ fontSize: 12, color: C.textMuted }}>
  👤 {typeof course.users === 'object' 
    ? (course.users?.full_name || course.users?.email || "Unknown creator")
    : "Unknown creator"}
</span>
                    </div>
                    {course.description && (
                      <p style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6, marginBottom: 8 }}>
                        {course.description.slice(0, 200)}{course.description.length > 200 ? "..." : ""}
                      </p>
                    )}
                    {course.mux_playback_id && (
                      <div style={{ fontSize: 11, color: C.textFaint }}>
                        Mux ID: {course.mux_playback_id.slice(0, 24)}...
                      </div>
                    )}
                  </div>
                </div>

                {/* Action area — only for pending */}
                {filter === "pending" && (
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                    <input
                      className="input-field"
                      placeholder="Optional review note..."
                      value={actionNote}
                      onChange={e => setActionNote(e.target.value)}
                      style={{ marginBottom: 10 }}
                    />
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        onClick={() => handleAction(course, "approve")}
                        disabled={processing === course.id}
                        style={{ padding: "9px 20px", borderRadius: 8, background: C.green, color: "#00171F", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body, opacity: processing === course.id ? 0.6 : 1 }}>
                        {processing === course.id ? "Processing..." : "✅ Approve & Publish"}
                      </button>
                      <button
                        onClick={() => handleAction(course, "reject")}
                        disabled={processing === course.id}
                        style={{ padding: "9px 20px", borderRadius: 8, background: "rgba(232,89,60,0.15)", color: C.red, border: `1px solid ${C.red}40`, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: F.body }}>
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}